// === IMPORTS ===
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  OfficeLayout,
  OfficeLocation,
  OfficeRoom,
  EditorTool,
  FurnitureType,
  PlacedFurniture,
  AIAgent,
  IsoTileType,
  FloorMaterialOption,
  InteractionTypeDefinition,
  AgentStatus,
  TaskItem,
  TimeMachineFrame,
  TimeMachineIncident,
  AgentInfoCategory,
  SystemTimezone,
  SystemSnapshot,
} from './types';
import {
  FURNITURE_CATALOG,
  DEFAULT_FLOOR_MATERIALS,
  DEFAULT_INTERACTION_TYPES,
  RoomTemplate,
} from './data/officeCatalog';
import { DEFAULT_LOCATIONS, DEFAULT_STARTUP_AGENTS } from './data/defaultLocations';
import { generateSeedTimeMachineHistory } from './data/timeMachineSeed';
import { findGridPath, getRoomCenter } from './utils/isoMath';
import { createNewOfficeRoom } from './utils/roomUtils';
import {
  DEFAULT_SYSTEM_TIMEZONES,
  formatTimeInTimezone,
  getTimezoneOffsetDisplay,
  POPULAR_IANA_TIMEZONES,
} from './utils/timezoneUtils';
import { DynamicActionType } from './components/AgentContextMenu';
import { IsometricCanvas } from './components/IsometricCanvas';
import { OfficeEditor } from './components/OfficeEditor';
import { OfficeSimulation } from './components/OfficeSimulation';
import { TimeMachineDrawer } from './components/TimeMachineDrawer';
import { SystemSnapshotDrawer } from './components/SystemSnapshotDrawer';
import { InteractionManagerModal } from './components/InteractionManagerModal';
import { LocationManagerDrawer } from './components/LocationManagerDrawer';
import { LocationSelectorBar } from './components/LocationSelectorBar';
import { TimezoneManagerModal } from './components/TimezoneManagerModal';
import { OverlapAlertModal } from './components/OverlapAlertModal';
import { ConfirmDeleteModal } from './components/ConfirmDeleteModal';
import { OfficeInspectorDrawer } from './components/OfficeInspectorDrawer';
import { AgentAvatarSpriteModal } from './components/AgentAvatarSpriteModal';
import { FloorMaterialDrawer } from './components/FloorMaterialDrawer';
import { UserAccountMenu, ConnectedUserProfile, DEFAULT_USER_PROFILE } from './components/UserAccountMenu';
import { UserSettingsDrawer } from './components/UserSettingsDrawer';
import { UserLogoutModal } from './components/UserLogoutModal';
import { SystemHealthBadge } from './components/SystemHealthBadge';
import { SystemHealthConfig, SystemHealthMetrics } from './types/systemHealth';
import { computeSystemHealthMetrics, loadHealthConfig, saveHealthConfig } from './utils/systemHealthEngine';
import { VisualThemeId } from './types/theme';
import { VISUAL_THEME_PRESETS, DEFAULT_THEME_ID } from './utils/themePresets';
import { Building2, Activity, Edit3, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import './AgentSimulator.css';
import './styles/twin-theme-variables.css';

// === COMPONENTE RAÍZ: ORQUESTADOR DEL AVAP DIGITAL TWIN ===
export default function AgentSimulator() {
  // Connected User Profile & Session State
  const [userProfile, setUserProfile] = useState<ConnectedUserProfile>(DEFAULT_USER_PROFILE);
  const [showUserSettingsDrawer, setShowUserSettingsDrawer] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const [isUserLoggedOut, setIsUserLoggedOut] = useState<boolean>(false);

  // Global Visual Theme State (Defaults to 'twopoint' - AVAP Digital Twin)
  const [visualThemeId, setVisualThemeId] = useState<VisualThemeId>(() => {
    try {
      const saved = localStorage.getItem('avap_visual_theme');
      if (saved && VISUAL_THEME_PRESETS.some((t) => t.id === saved)) {
        return saved as VisualThemeId;
      }
    } catch (e) {
      // ignore
    }
    return DEFAULT_THEME_ID;
  });

  // Apply theme to DOM data-theme attribute
  useEffect(() => {
    try {
      localStorage.setItem('avap_visual_theme', visualThemeId);
      document.documentElement.setAttribute('data-theme', visualThemeId);
      document.body.setAttribute('data-theme', visualThemeId);
    } catch (e) {
      // ignore
    }
  }, [visualThemeId]);

  // App Mode: 'editor' (Diagramador) or 'simulation' (Entorno Tiempo Real)
  const [mode, setMode] = useState<'editor' | 'simulation'>('editor');

  // Left Operations & Configuration Sidebar Curtain Collapse State
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);

  // Confirm Reset / Clean Grid Modal State
  const [showResetCleanGridModal, setShowResetCleanGridModal] = useState<boolean>(false);

  // Room Overlap Warning Modal State
  const [overlapModal, setOverlapModal] = useState<{
    isOpen: boolean;
    targetRoomName?: string;
    conflictingRoomName?: string;
    actionContext?: 'create' | 'resize' | 'move' | 'edit';
  }>({
    isOpen: false,
  });

  const handleOverlapPrevented = useCallback(
    (targetRoomName: string, conflictingRoomName: string, actionContext: 'create' | 'resize' | 'move' | 'edit' = 'resize') => {
      setOverlapModal({
        isOpen: true,
        targetRoomName,
        conflictingRoomName,
        actionContext,
      });
    },
    []
  );

  // Interaction Types State & Modal
  const [interactionTypes, setInteractionTypes] = useState<InteractionTypeDefinition[]>(DEFAULT_INTERACTION_TYPES);
  const [showInteractionManagerModal, setShowInteractionManagerModal] = useState<boolean>(false);
  const [interactionManagerTab, setInteractionManagerTab] = useState<'catalog' | 'create' | 'guide'>('catalog');

  const handleSaveInteraction = useCallback((interaction: InteractionTypeDefinition) => {
    setInteractionTypes((prev) => {
      const idx = prev.findIndex((i) => i.id === interaction.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = interaction;
        return copy;
      }
      return [...prev, interaction];
    });
  }, []);

  const handleDeleteInteraction = useCallback((id: string) => {
    setInteractionTypes((prev) => prev.filter((i) => i.id !== id));
  }, []);

  // Multi-Location State (Each location is an independent canvas/grid)
  const STORAGE_KEY = 'iso_office_locations_v17_16sprites';
  const STORAGE_TZ_KEY = 'avap_system_timezones_v1';

  // System Timezones State (Saved global timezones)
  const [systemTimezones, setSystemTimezones] = useState<SystemTimezone[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_TZ_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Error reading system timezones from localStorage', e);
    }
    return DEFAULT_SYSTEM_TIMEZONES;
  });

  const [locations, setLocations] = useState<OfficeLocation[]>(() => {
    try {
      [
        'iso_office_locations_v1',
        'iso_office_locations_v2',
        'iso_office_locations_v3',
        'iso_office_locations_v4',
        'iso_office_locations_v5_clean_empty',
        'iso_office_locations_v12_empty_clean_4_agents',
        'iso_office_locations_v14_default_20x20',
        'iso_office_locations_v16_npc_purple_sprite',
      ].forEach((k) => {
        try {
          localStorage.removeItem(k);
        } catch (_) {}
      });

      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((loc: OfficeLocation) => {
            const agentsList =
              Array.isArray(loc.agents) && loc.agents.length > 0
                ? [...loc.agents.filter((ag: any) => !ag.isNpc && ag.id !== 'agent_npc_kai')]
                : [...DEFAULT_STARTUP_AGENTS.map((ag) => ({ ...ag, id: `${ag.id}_${loc.id}` }))];

            return {
              ...loc,
              timezone: loc.timezone || 'Europe/Madrid',
              agents: agentsList,
            };
          });
        }
      }
    } catch (e) {
      console.warn('Error loading locations from localStorage', e);
    }
    return DEFAULT_LOCATIONS;
  });

  const [activeLocationId, setActiveLocationId] = useState<string>(() => locations[0]?.id || DEFAULT_LOCATIONS[0].id);
  const [showLocationManagerDrawer, setShowLocationManagerDrawer] = useState<boolean>(false);
  const [locationManagerTab, setLocationManagerTab] = useState<'details' | 'list' | 'create' | 'transfer'>('details');

  // Active Location (Lienzo / Grid activo)
  const activeLocation = useMemo(() => {
    return locations.find((l) => l.id === activeLocationId) || locations[0] || DEFAULT_LOCATIONS[0];
  }, [locations, activeLocationId]);

  // System Health Telemetry & Configuration State
  const [healthConfig, setHealthConfig] = useState<SystemHealthConfig>(() => loadHealthConfig());
  const [settingsDrawerInitialTab, setSettingsDrawerInitialTab] = useState<'visual' | 'health' | 'profile' | 'system' | 'shortcuts'>('visual');

  const healthMetrics = useMemo<SystemHealthMetrics>(() => {
    return computeSystemHealthMetrics(activeLocation, healthConfig, 60);
  }, [activeLocation, healthConfig]);

  const handleSaveHealthConfig = useCallback((newConfig: SystemHealthConfig) => {
    setHealthConfig(newConfig);
    saveHealthConfig(newConfig);
  }, []);

  // Active System Timezone
  const [activeTimezoneIana, setActiveTimezoneIana] = useState<string>(() => {
    return locations[0]?.timezone || DEFAULT_SYSTEM_TIMEZONES[0]?.iana || 'Europe/Madrid';
  });
  const [showTimezoneModal, setShowTimezoneModal] = useState<boolean>(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_TZ_KEY, JSON.stringify(systemTimezones));
    } catch (e) {
      console.warn('Error saving timezones to localStorage', e);
    }
  }, [systemTimezones]);

  const handleEnsureTimezoneRegistered = useCallback((iana: string) => {
    setSystemTimezones((prev) => {
      if (prev.some((tz) => tz.iana === iana)) return prev;
      const matched = POPULAR_IANA_TIMEZONES.find((p) => p.iana === iana);
      const newTz: SystemTimezone = {
        id: `tz_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
        name: matched?.name || iana,
        iana,
        flag: matched?.flag || '🌐',
        city: matched?.city,
        country: matched?.country,
      };
      return [...prev, newTz];
    });
  }, []);

  const handleSelectActiveTimezone = useCallback((iana: string) => {
    setActiveTimezoneIana(iana);
  }, []);

  const handleAddTimezone = useCallback((newTz: SystemTimezone) => {
    setSystemTimezones((prev) => {
      if (prev.some((tz) => tz.iana === newTz.iana)) return prev;
      return [...prev, newTz];
    });
    setActiveTimezoneIana(newTz.iana);
  }, []);

  const handleDeleteTimezone = useCallback(
    (id: string) => {
      setSystemTimezones((prev) => {
        if (prev.length <= 1) return prev;
        const filtered = prev.filter((tz) => tz.id !== id);
        if (!filtered.some((tz) => tz.iana === activeTimezoneIana)) {
          setActiveTimezoneIana(filtered[0]?.iana || 'Europe/Madrid');
        }
        return filtered;
      });
    },
    [activeTimezoneIana]
  );

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(locations));
    } catch (e) {
      console.warn('Error saving locations to localStorage', e);
    }
  }, [locations]);

  // Reset current floor to pristine clean grid with 4 available agents
  const handleResetActiveFloorToClean = useCallback(() => {
    const cleanAgents = DEFAULT_STARTUP_AGENTS.map((ag) => ({
      ...ag,
      id: `${ag.id}_${Date.now()}`,
      isPlaced: false,
      currentCol: -1,
      currentRow: -1,
      targetCol: null,
      targetRow: null,
      path: [],
      status: 'idle' as const,
    }));

    setLocations((prevLocations) => {
      return prevLocations.map((loc) => {
        if (loc.id !== activeLocationId) return loc;
        return {
          ...loc,
          customFloors: {},
          rooms: [],
          items: [],
          agents: cleanAgents,
        };
      });
    });
    setSelectedItem(null);
    setSelectedAgent(null);
    setSelectedRoomId(null);
    const resetLoc: OfficeLocation = {
      ...activeLocation,
      customFloors: {},
      rooms: [],
      items: [],
      agents: cleanAgents,
    };
    setEditorHistory([resetLoc]);
    setEditorHistoryIndex(0);
    editorHistoryRef.current = [resetLoc];
    editorHistoryIndexRef.current = 0;

    setSimulationHistory([resetLoc]);
    setSimulationHistoryIndex(0);
    simulationHistoryRef.current = [resetLoc];
    simulationHistoryIndexRef.current = 0;
  }, [activeLocationId, activeLocation]);

  // Separate Undo Histories for Editor (Diagramador) and Simulation (Simulador)
  const [editorHistory, setEditorHistory] = useState<OfficeLayout[]>([activeLocation]);
  const [editorHistoryIndex, setEditorHistoryIndex] = useState<number>(0);
  const editorHistoryRef = useRef<OfficeLayout[]>([activeLocation]);
  const editorHistoryIndexRef = useRef<number>(0);
  editorHistoryRef.current = editorHistory;
  editorHistoryIndexRef.current = editorHistoryIndex;

  const [simulationHistory, setSimulationHistory] = useState<OfficeLayout[]>([activeLocation]);
  const [simulationHistoryIndex, setSimulationHistoryIndex] = useState<number>(0);
  const simulationHistoryRef = useRef<OfficeLayout[]>([activeLocation]);
  const simulationHistoryIndexRef = useRef<number>(0);
  simulationHistoryRef.current = simulationHistory;
  simulationHistoryIndexRef.current = simulationHistoryIndex;

  // Whenever mode transitions (editor <-> simulation), forget previous actions and start fresh with undo disabled
  const prevModeRef = useRef<'editor' | 'simulation'>(mode);
  useEffect(() => {
    if (prevModeRef.current !== mode) {
      prevModeRef.current = mode;
      const currentLoc = locations.find((l) => l.id === activeLocationId) || locations[0] || activeLocation;
      if (mode === 'editor') {
        setEditorHistory([currentLoc]);
        setEditorHistoryIndex(0);
        editorHistoryRef.current = [currentLoc];
        editorHistoryIndexRef.current = 0;
      } else {
        setSimulationHistory([currentLoc]);
        setSimulationHistoryIndex(0);
        simulationHistoryRef.current = [currentLoc];
        simulationHistoryIndexRef.current = 0;
      }
    }
  }, [mode, locations, activeLocationId, activeLocation]);

  // Helper to reset both histories on location change
  const resetBothHistories = useCallback((loc: OfficeLocation) => {
    setEditorHistory([loc]);
    setEditorHistoryIndex(0);
    editorHistoryRef.current = [loc];
    editorHistoryIndexRef.current = 0;

    setSimulationHistory([loc]);
    setSimulationHistoryIndex(0);
    simulationHistoryRef.current = [loc];
    simulationHistoryIndexRef.current = 0;
  }, []);

  // Function to commit layout changes to active location and history
  const updateLayout = useCallback(
    (newLayoutOrFn: OfficeLayout | ((prev: OfficeLayout) => OfficeLayout), recordHistory: boolean = true) => {
      setLocations((prevLocations) => {
        const currentLoc = prevLocations.find((l) => l.id === activeLocationId) || prevLocations[0];
        const nextLayout = typeof newLayoutOrFn === 'function' ? newLayoutOrFn(currentLoc) : newLayoutOrFn;
        if (nextLayout === currentLoc) return prevLocations;

        if (recordHistory) {
          if (mode === 'editor') {
            const currentIdx = editorHistoryIndexRef.current;
            const currentHist = editorHistoryRef.current;

            const newHistory = currentHist.slice(0, currentIdx + 1);
            newHistory.push(nextLayout);

            if (newHistory.length > 50) {
              newHistory.shift();
            }

            editorHistoryRef.current = newHistory;
            editorHistoryIndexRef.current = newHistory.length - 1;

            queueMicrotask(() => {
              setEditorHistory(newHistory);
              setEditorHistoryIndex(newHistory.length - 1);
              setSelectedItem((prev) => (prev && nextLayout.items.some((it) => it.id === prev.id) ? prev : null));
              setSelectedAgent((prev) => (prev && nextLayout.agents.some((ag) => ag.id === prev.id) ? prev : null));
              setSelectedRoomId((prev) => (prev && nextLayout.rooms?.some((r) => r.id === prev) ? prev : null));
            });
          } else {
            const currentIdx = simulationHistoryIndexRef.current;
            const currentHist = simulationHistoryRef.current;

            const newHistory = currentHist.slice(0, currentIdx + 1);
            newHistory.push(nextLayout);

            if (newHistory.length > 50) {
              newHistory.shift();
            }

            simulationHistoryRef.current = newHistory;
            simulationHistoryIndexRef.current = newHistory.length - 1;

            queueMicrotask(() => {
              setSimulationHistory(newHistory);
              setSimulationHistoryIndex(newHistory.length - 1);
              setSelectedItem((prev) => (prev && nextLayout.items.some((it) => it.id === prev.id) ? prev : null));
              setSelectedAgent((prev) => (prev && nextLayout.agents.some((ag) => ag.id === prev.id) ? prev : null));
              setSelectedRoomId((prev) => (prev && nextLayout.rooms?.some((r) => r.id === prev) ? prev : null));
            });
          }
        }

        return prevLocations.map((l) => (l.id === currentLoc.id ? { ...l, ...nextLayout } : l));
      });
    },
    [activeLocationId, mode]
  );

  // Location Handlers
  const handleSelectLocation = (locationId: string) => {
    const targetLoc = locations.find((l) => l.id === locationId);
    if (!targetLoc) return;
    setActiveLocationId(locationId);
    if (targetLoc.timezone) {
      handleEnsureTimezoneRegistered(targetLoc.timezone);
      setActiveTimezoneIana(targetLoc.timezone);
    }
    setSelectedItem(null);
    setSelectedAgent(null);
    setSelectedRoomId(null);
    setRoomDraftBounds(null);
    resetBothHistories(targetLoc);
  };

  const handleCreateLocation = (newLoc: OfficeLocation) => {
    setLocations((prev) => [...prev, newLoc]);
    setActiveLocationId(newLoc.id);
    if (newLoc.timezone) {
      handleEnsureTimezoneRegistered(newLoc.timezone);
      setActiveTimezoneIana(newLoc.timezone);
    }
    setSelectedItem(null);
    setSelectedAgent(null);
    setSelectedRoomId(null);
    setRoomDraftBounds(null);
    resetBothHistories(newLoc);
  };

  const handleUpdateLocation = (updatedLoc: OfficeLocation) => {
    setLocations((prev) => prev.map((l) => (l.id === updatedLoc.id ? updatedLoc : l)));
  };

  const handleDeleteLocation = (locationId: string) => {
    if (locations.length <= 1) return;
    const remaining = locations.filter((l) => l.id !== locationId);
    setLocations(remaining);
    if (activeLocationId === locationId) {
      setActiveLocationId(remaining[0].id);
      resetBothHistories(remaining[0]);
    }
  };

  const handleDuplicateLocation = (locationId: string) => {
    const locToClone = locations.find((l) => l.id === locationId);
    if (!locToClone) return;
    const cloned: OfficeLocation = {
      ...locToClone,
      id: `loc_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      name: `${locToClone.name} (Copia)`,
      rooms: locToClone.rooms?.map((r) => ({ ...r, id: `${r.id}_${Date.now()}` })),
      items: locToClone.items?.map((it) => ({ ...it, id: `${it.id}_${Date.now()}` })),
      agents: locToClone.agents?.map((ag) => ({ ...ag, id: `${ag.id}_${Date.now()}` })),
    };
    setLocations((prev) => [...prev, cloned]);
    setActiveLocationId(cloned.id);
    resetBothHistories(cloned);
  };

  const handleTransferAgent = (agentId: string, fromLocId: string, toLocId: string) => {
    if (fromLocId === toLocId) return;
    const fromLoc = locations.find((l) => l.id === fromLocId);
    const toLoc = locations.find((l) => l.id === toLocId);
    if (!fromLoc || !toLoc) return;
    const agent = fromLoc.agents.find((a) => a.id === agentId);
    if (!agent) return;

    const updatedFromLoc: OfficeLocation = {
      ...fromLoc,
      agents: fromLoc.agents.filter((a) => a.id !== agentId),
    };

    const targetCol = Math.min(agent.currentCol, toLoc.gridWidth - 1);
    const targetRow = Math.min(agent.currentRow, toLoc.gridHeight - 1);

    const transferredAgent: AIAgent = {
      ...agent,
      currentCol: targetCol,
      currentRow: targetRow,
      homeCol: targetCol,
      homeRow: targetRow,
      assignedDeskId: null,
      path: [],
      status: 'idle' as const,      
      thoughts: [{ time: 'Ahora', text: `Transferido a ${toLoc.name}.`, mood: 'happy' }],
    };

    const updatedToLoc: OfficeLocation = {
      ...toLoc,
      agents: [...toLoc.agents, transferredAgent],
    };

    setLocations((prev) =>
      prev.map((l) => {
        if (l.id === fromLocId) return updatedFromLoc;
        if (l.id === toLocId) return updatedToLoc;
        return l;
      })
    );
  };

  // Reference to top header and canvas workspace area to dynamically center mode switcher above the canvas
  const headerRef = useRef<HTMLElement>(null);
  const canvasAreaRef = useRef<HTMLDivElement>(null);
  const [canvasCenterOffset, setCanvasCenterOffset] = useState<number | null>(null);

  // System Snapshot & JSON State Drawer
  const [showSystemSnapshotDrawer, setShowSystemSnapshotDrawer] = useState<boolean>(false);

  // Restore snapshot handler
  const handleRestoreSnapshot = useCallback(
    (snapshot: SystemSnapshot) => {
      if (!snapshot || !snapshot.locations || snapshot.locations.length === 0) return;

      setLocations(snapshot.locations);

      const targetLocId = snapshot.metadata.activeLocationId || snapshot.locations[0].id;
      const targetLoc = snapshot.locations.find((l) => l.id === targetLocId) || snapshot.locations[0];
      setActiveLocationId(targetLoc.id);

      resetBothHistories(targetLoc);

      if (snapshot.systemTimezones && snapshot.systemTimezones.length > 0) {
        setSystemTimezones(snapshot.systemTimezones);
      }
      if (snapshot.metadata.activeTimezoneIana) {
        setActiveTimezoneIana(snapshot.metadata.activeTimezoneIana);
      }

      if (snapshot.simulation) {
        if (typeof snapshot.simulation.isPlaying === 'boolean') {
          setIsSimulationPlaying(snapshot.simulation.isPlaying);
        }
        if (snapshot.simulation.timeMachineFrames && snapshot.simulation.timeMachineFrames.length > 0) {
          setTimeMachineData({
            frames: snapshot.simulation.timeMachineFrames,
            incidents: snapshot.simulation.incidents || [],
          });
          setTimeMachineFrameIndex(snapshot.simulation.currentTimeMachineFrameIndex ?? snapshot.simulation.timeMachineFrames.length - 1);
          setIsTimeMachineLive(snapshot.simulation.isTimeMachineLive ?? true);
        }
      }

      if (snapshot.metadata.mode) {
        setMode(snapshot.metadata.mode);
      }

      setSelectedItem(null);
      setSelectedAgent(null);
      setSelectedRoomId(null);
    },
    [resetBothHistories]
  );

  const updateCanvasCenter = useCallback(() => {
    if (canvasAreaRef.current && headerRef.current) {
      const canvasRect = canvasAreaRef.current.getBoundingClientRect();
      const headerRect = headerRef.current.getBoundingClientRect();
      const centerX = canvasRect.left + canvasRect.width / 2 - headerRect.left;
      setCanvasCenterOffset(centerX);
    }
  }, []);

  useEffect(() => {
    updateCanvasCenter();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && canvasAreaRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updateCanvasCenter();
      });
      resizeObserver.observe(canvasAreaRef.current);
    }

    window.addEventListener('resize', updateCanvasCenter);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateCanvasCenter);
    };
  }, [updateCanvasCenter]);

  // When sidebar collapses or expands (curtain transition), continuously update the center offset
  useEffect(() => {
    updateCanvasCenter();
    const startTime = performance.now();
    let animId: number;
    const tick = (now: number) => {
      updateCanvasCenter();
      if (now - startTime < 350) {
        animId = requestAnimationFrame(tick);
      }
    };
    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, [isSidebarOpen, mode, updateCanvasCenter]);

  // Handle Undo - strictly per current active mode
  const handleUndo = useCallback(() => {
    if (mode === 'editor') {
      const currentIdx = editorHistoryIndexRef.current;
      const currentHist = editorHistoryRef.current;

      if (currentIdx > 0 && currentHist[currentIdx - 1]) {
        const targetLayout = currentHist[currentIdx - 1];
        setEditorHistoryIndex(currentIdx - 1);
        editorHistoryIndexRef.current = currentIdx - 1;
        setLocations((prev) => prev.map((l) => (l.id === activeLocationId ? { ...l, ...targetLayout } : l)));

        setSelectedItem((item) => (item && targetLayout.items.some((it) => it.id === item.id) ? item : null));
        setSelectedAgent((agent) => (agent && targetLayout.agents.some((ag) => ag.id === agent.id) ? agent : null));
        setSelectedRoomId((roomId) => (roomId && targetLayout.rooms?.some((r) => r.id === roomId) ? roomId : null));
      }
    } else {
      const currentIdx = simulationHistoryIndexRef.current;
      const currentHist = simulationHistoryRef.current;

      if (currentIdx > 0 && currentHist[currentIdx - 1]) {
        const targetLayout = currentHist[currentIdx - 1];
        setSimulationHistoryIndex(currentIdx - 1);
        simulationHistoryIndexRef.current = currentIdx - 1;
        setLocations((prev) => prev.map((l) => (l.id === activeLocationId ? { ...l, ...targetLayout } : l)));

        setSelectedItem((item) => (item && targetLayout.items.some((it) => it.id === item.id) ? item : null));
        setSelectedAgent((agent) => (agent && targetLayout.agents.some((ag) => ag.id === agent.id) ? agent : null));
        setSelectedRoomId((roomId) => (roomId && targetLayout.rooms?.some((r) => r.id === roomId) ? roomId : null));
      }
    }
  }, [activeLocationId, mode]);

  const canUndo = mode === 'editor' ? editorHistoryIndex > 0 : simulationHistoryIndex > 0;

  // Global Ctrl+Z / Cmd+Z shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z') && !e.shiftKey) {
        const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || (e.target as HTMLElement)?.isContentEditable) {
          return;
        }
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo]);

  // Floor Materials state
  const [floorMaterials, setFloorMaterials] = useState<FloorMaterialOption[]>(DEFAULT_FLOOR_MATERIALS);

  const floorMaterialsMap = useMemo(() => {
    const map: Record<string, { customImage?: string }> = {};
    floorMaterials.forEach((fm) => {
      map[fm.id] = { customImage: fm.customImage };
    });
    return map;
  }, [floorMaterials]);

  // Editor Selection & Tool State
  const [activeTool, setActiveTool] = useState<EditorTool>('select');
  const [selectedFurnitureType, setSelectedFurnitureType] = useState<FurnitureType | null>(null);
  const [selectedFloorType, setSelectedFloorType] = useState<IsoTileType | null>(null);
  const [currentRoomName, setCurrentRoomName] = useState<string>('Main Office');
  const [selectedItem, setSelectedItem] = useState<PlacedFurniture | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<AIAgent | null>(null);
  const [selectedAgentModalTab, setSelectedAgentModalTab] = useState<AgentInfoCategory | undefined>(undefined);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [activeEditorStep, setActiveEditorStep] = useState<1 | 2 | 3 | 4>(1);
  const [isInspectorDrawerOpen, setIsInspectorDrawerOpen] = useState<boolean>(false);

  // Inspector Drawer sub-modals state
  const [floorToEdit, setFloorToEdit] = useState<FloorMaterialOption | null>(null);
  const [floorToDelete, setFloorToDelete] = useState<FloorMaterialOption | null>(null);
  const [roomToDelete, setRoomToDelete] = useState<OfficeRoom | null>(null);
  const [itemToDelete] = useState<PlacedFurniture | null>(null);
  const [furnitureToDelete, setFurnitureToDelete] = useState<{ type: FurnitureType; name?: string; icon?: string } | null>(null);
  const [agentToDelete, setAgentToDelete] = useState<AIAgent | null>(null);
  const [spriteModalAgent, setSpriteModalAgent] = useState<AIAgent | null>(null);

  const furnitureInstancesCount = useMemo(() => {
    if (!furnitureToDelete) return 0;
    return locations.reduce((acc, loc) => {
      return acc + (loc.items?.filter((it) => it.type === furnitureToDelete.type).length || 0);
    }, 0);
  }, [furnitureToDelete, locations]);

  const handleConfirmDeleteFurniture = useCallback(() => {
    if (!furnitureToDelete) return;
    const targetType = furnitureToDelete.type;

    setLocations((prevLocations) =>
      prevLocations.map((loc) => ({
        ...loc,
        items: (loc.items || []).filter((it) => it.type !== targetType),
      }))
    );

    const catalogIdx = FURNITURE_CATALOG.findIndex((f) => f.type === targetType);
    if (catalogIdx >= 0) {
      FURNITURE_CATALOG.splice(catalogIdx, 1);
    }

    if (selectedFurnitureType === targetType) {
      setSelectedFurnitureType(null);
    }
    if (selectedItem?.type === targetType) {
      setSelectedItem(null);
    }
    setIsInspectorDrawerOpen(false);
    setFurnitureToDelete(null);
  }, [furnitureToDelete, selectedFurnitureType, selectedItem]);

  const floorTilesCount = useMemo(() => {
    if (!floorToDelete) return 0;
    return locations.reduce((acc, loc) => {
      return acc + ((loc as any).floorTiles?.filter((tile: any) => tile.materialId === floorToDelete.id || tile.floorType === floorToDelete.id).length || 0);
    }, 0);
  }, [floorToDelete, locations]);

  const handleConfirmDeleteFloor = useCallback(() => {
    if (!floorToDelete) return;
    const materialId = floorToDelete.id;

    setFloorMaterials((prev) => prev.filter((fm) => fm.id !== materialId));

    setLocations((prevLocations) =>
      prevLocations.map((loc) => {
        const updatedFloorTiles = ((loc as any).floorTiles || []).filter(
          (tile: any) => tile.materialId !== materialId && tile.floorType !== materialId
        );
        let updatedMaterialsMap = (loc as any).floorMaterialsMap;
        if (updatedMaterialsMap) {
          const newMap: Record<string, string> = {};
          Object.entries(updatedMaterialsMap).forEach(([k, v]) => {
            if (v !== materialId && typeof v === 'string') {
              newMap[k] = v;
            }
          });
          updatedMaterialsMap = newMap;
        }
        return {
          ...loc,
          floorTiles: updatedFloorTiles,
          floorMaterialsMap: updatedMaterialsMap,
        };
      })
    );

    if (selectedFloorType === materialId) {
      setSelectedFloorType(null);
    }
    setIsInspectorDrawerOpen(false);
    setFloorToDelete(null);
  }, [floorToDelete, selectedFloorType]);

  // Explicit handlers that open drawer when an item is selected from UI
  const handleSelectRoomWithDrawer = useCallback((id: string | null) => {
    setSelectedRoomId(id);
    if (id) {
      setIsInspectorDrawerOpen(true);
    } else {
      setIsInspectorDrawerOpen(false);
    }
  }, []);

  const handleSelectFloorTypeWithDrawer = useCallback((type: IsoTileType | null) => {
    setSelectedFloorType(type);
    if (type) {
      setIsInspectorDrawerOpen(true);
    }
  }, []);

  const handleSelectFurnitureTypeWithDrawer = useCallback((type: FurnitureType | null) => {
    setSelectedFurnitureType(type);
    if (type) {
      setIsInspectorDrawerOpen(true);
    }
  }, []);

  const handleSelectItemWithDrawer = useCallback((item: PlacedFurniture | null) => {
    setSelectedItem(item);
    if (item) {
      setIsInspectorDrawerOpen(true);
    }
  }, []);

  const handleSelectAgentWithDrawer = useCallback((agent: AIAgent | null, initialTab?: AgentInfoCategory) => {
    setSelectedAgent(agent);
    setSelectedAgentModalTab(initialTab);
    if (agent) {
      setIsInspectorDrawerOpen(true);
    }
  }, []);

  const handleSelectAgentModal = (agent: AIAgent | null, initialTab?: 
    AgentInfoCategory) => {
    setSelectedAgent(agent);
    setSelectedAgentModalTab(initialTab);
    if (agent && mode === 'editor') {
      setIsInspectorDrawerOpen(true);
    }
  };
  const [roomDraftBounds, setRoomDraftBounds] = useState<{
    col: number;
    row: number;
    width: number;
    height: number;
    color?: string;
    name?: string;
    icon?: string;
  } | null>(null);

  // Move agent directly to room center
  const handleMoveAgentToRoom = (agentId: string, room: OfficeRoom) => {
    updateLayout((prev) => {
      const center = getRoomCenter(room);
      return {
        ...prev,
        agents: prev.agents.map((ag) => {
          if (ag.id !== agentId) return ag;
          return {
            ...ag,
            targetCol: center.col,
            targetRow: center.row,
            status: 'walking' as const,
            statusTimer: 5,
          };
        }),
      };
    });
  };

  // Handle Tile Click in Canvas during Editing
  const handleTileClick = (col: number, row: number) => {
    if (mode !== 'editor') return;

    setIsInspectorDrawerOpen(false);

    if (selectedAgent) {
      updateLayout((prev) => ({
        ...prev,
        agents: prev.agents.map((ag) =>
          ag.id === selectedAgent.id
            ? {
                ...ag,
                isPlaced: true,
                currentCol: col,
                currentRow: row,
                homeCol: col,
                homeRow: row,
                renderX: col,
                renderY: row,
                targetCol: null,
                targetRow: null,
                path: [],
                status: 'idle' as const,
              }
            : ag
        ),
      }));
      setSelectedAgent(null);
      return;
    }

    if (activeTool === 'place' && selectedFurnitureType) {
      const def = FURNITURE_CATALOG.find((f) => f.type === selectedFurnitureType);
      const itemW = def?.width || 1;
      const itemH = def?.height || 1;

      const newItem: PlacedFurniture = {
        id: `item_${Date.now()}`,
        type: selectedFurnitureType,
        col,
        row,
        rotation: 0,
        width: itemW,
        height: itemH,
        customImage: def?.customImage,
      };

      updateLayout((prev) => ({
        ...prev,
        items: [...prev.items, newItem],
      }));
    }
  };

  // Handle Furniture Drop on Diagram
  const handleDropFurniture = (type: FurnitureType, col: number, row: number) => {
    setIsInspectorDrawerOpen(false);
    const def = FURNITURE_CATALOG.find((f) => f.type === type);
    const newItem: PlacedFurniture = {
      id: `item_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      type,
      col,
      row,
      rotation: 0,
      width: def?.width || 1,
      height: def?.height || 1,
      customImage: def?.customImage,
    };

    updateLayout((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
    setSelectedItem(newItem);
    setSelectedAgent(null);
    if (mode === 'editor') {
      setActiveTool('select');
    }
  };

  // Handle Agent Drop on Diagram
  const handleDropAgent = (agentId: string, col: number, row: number) => {
    setIsInspectorDrawerOpen(false);
    updateLayout((prev) => {
      const updatedAgents = prev.agents.map((ag) =>
        ag.id === agentId
          ? {
              ...ag,
              isPlaced: true,
              currentCol: col,
              currentRow: row,
              homeCol: col,
              homeRow: row,
              renderX: col,
              renderY: row,
              targetCol: null,
              targetRow: null,
              path: [],
              status: 'idle' as const,
            }
          : ag
      );
      return {
        ...prev,
        agents: updatedAgents,
      };
    });

    const placedAgent = activeLocation.agents.find((a) => a.id === agentId);
    if (placedAgent) {
      setSelectedAgent({
        ...placedAgent,
        isPlaced: true,
        currentCol: col,
        currentRow: row,
        homeCol: col,
        homeRow: row,
        renderX: col,
        renderY: row,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'idle',
      });
    }
  };

  // Handle Room Dimension/Position Interactive Update (from Canvas Vertex Dragging)
  const handleRoomUpdate = useCallback(
    (updatedRoom: OfficeRoom) => {
      if (mode !== 'editor') return;
      setIsInspectorDrawerOpen(false);
      updateLayout((prev) => ({
        ...prev,
        rooms: (prev.rooms || []).map((r) => (r.id === updatedRoom.id ? updatedRoom : r)),
      }));
    },
    [mode, updateLayout]
  );

  // Handle Room Deletion
  const handleDeleteRoom = useCallback(
    (roomId: string) => {
      updateLayout((prev) => ({
        ...prev,
        rooms: (prev.rooms || []).filter((r) => r.id !== roomId),
      }));
      setSelectedRoomId((prev) => (prev === roomId ? null : prev));
    },
    [updateLayout]
  );

  // Handle Floor Tile Paint/Erase
  const handlePaintTile = (col: number, row: number, isErasing: boolean) => {
    if (mode !== 'editor') return;
    if (!isErasing && !selectedFloorType) return;

    setIsInspectorDrawerOpen(false);

    updateLayout((prev) => {
      const customFloors = { ...(prev.customFloors || {}) };
      const key = `${col},${row}`;

      if (isErasing) {
        delete customFloors[key];
        delete customFloors[`${col}_${row}`];
      } else if (selectedFloorType) {
        const selectedMat = floorMaterials.find((f) => f.id === selectedFloorType);
        customFloors[key] = {
          tileType: selectedFloorType,
          customImage: selectedMat?.customImage,
        };
      }

      return {
        ...prev,
        customFloors,
      };
    });
  };

  // Clear EVERYTHING on a specific tile (floor, items, agents)
  const handleClearTile = useCallback(
    (col: number, row: number) => {
      setIsInspectorDrawerOpen(false);
      updateLayout((prev) => {
        const customFloors = { ...(prev.customFloors || {}) };
        delete customFloors[`${col},${row}`];
        delete customFloors[`${col}_${row}`];

        const items = prev.items.filter((item) => {
          const def = FURNITURE_CATALOG.find((f) => f.type === item.type);
          const w = item.width || def?.width || 1;
          const h = item.height || def?.height || 1;
          const occupies = col >= item.col && col < item.col + w && row >= item.row && row < item.row + h;
          return !occupies;
        });

        const agents = prev.agents.map((ag) =>
          ag.currentCol === col && ag.currentRow === row
            ? {
                ...ag,
                isPlaced: false,
                currentCol: -1,
                currentRow: -1,
                targetCol: null,
                targetRow: null,
                path: [],
                status: 'idle' as const,
              }
            : ag
        );

        return {
          ...prev,
          customFloors,
          items,
          agents,
        };
      });

      setSelectedItem((prev) => {
        if (!prev) return null;
        const def = FURNITURE_CATALOG.find((f) => f.type === prev.type);
        const w = prev.width || def?.width || 1;
        const h = prev.height || def?.height || 1;
        if (col >= prev.col && col < prev.col + w && row >= prev.row && row < prev.row + h) {
          return null;
        }
        return prev;
      });
      setSelectedAgent((prev) => (prev && prev.currentCol === col && prev.currentRow === row ? null : prev));
    },
    [updateLayout]
  );

  // Clear only furniture on a specific tile
  const handleClearTileFurniture = useCallback(
    (col: number, row: number) => {
      setIsInspectorDrawerOpen(false);
      updateLayout((prev) => {
        const items = prev.items.filter((item) => {
          const def = FURNITURE_CATALOG.find((f) => f.type === item.type);
          const w = item.width || def?.width || 1;
          const h = item.height || def?.height || 1;
          const occupies = col >= item.col && col < item.col + w && row >= item.row && row < item.row + h;
          return !occupies;
        });
        return { ...prev, items };
      });
      setSelectedItem((prev) => {
        if (!prev) return null;
        const def = FURNITURE_CATALOG.find((f) => f.type === prev.type);
        const w = prev.width || def?.width || 1;
        const h = prev.height || def?.height || 1;
        if (col >= prev.col && col < prev.col + w && row >= prev.row && row < prev.row + h) {
          return null;
        }
        return prev;
      });
    },
    [updateLayout]
  );

  // Clear only custom floor on a specific tile
  const handleClearTileFloor = useCallback(
    (col: number, row: number) => {
      setIsInspectorDrawerOpen(false);
      updateLayout((prev) => {
        const customFloors = { ...(prev.customFloors || {}) };
        delete customFloors[`${col},${row}`];
        delete customFloors[`${col}_${row}`];
        return { ...prev, customFloors };
      });
    },
    [updateLayout]
  );

  // Clear only agent on a specific tile (unplace and return to available pool)
  const handleClearTileAgent = useCallback(
    (col: number, row: number) => {
      setIsInspectorDrawerOpen(false);
      updateLayout((prev) => ({
        ...prev,
        agents: prev.agents.map((ag) =>
          ag.currentCol === col && ag.currentRow === row
            ? {
                ...ag,
                isPlaced: false,
                currentCol: -1,
                currentRow: -1,
                targetCol: null,
                targetRow: null,
                path: [],
                status: 'idle' as const,
              }
            : ag
        ),
      }));
      setSelectedAgent((prev) => (prev && prev.currentCol === col && prev.currentRow === row ? null : prev));
    },
    [updateLayout]
  );

  // Set floor type directly on a specific tile
  const handleSetTileFloor = useCallback(
    (col: number, row: number, tileType: IsoTileType) => {
      setIsInspectorDrawerOpen(false);
      updateLayout((prev) => {
        const customFloors = { ...(prev.customFloors || {}) };
        const selectedMat = floorMaterials.find((f) => f.id === tileType);
        customFloors[`${col},${row}`] = {
          tileType,
          roomName: currentRoomName || 'Zona',
          customImage: selectedMat?.customImage,
        };
        return { ...prev, customFloors };
      });
    },
    [updateLayout, floorMaterials, currentRoomName]
  );

  // Handle Room Creation (Unified canonical logic used by RoomManager and TileContextMenu)
  const handleCreateRoom = useCallback(
    (col?: number, row?: number, template?: RoomTemplate | Partial<OfficeRoom>) => {
      setMode('editor');
      setRoomDraftBounds(null);
      setActiveTool('select');
      setIsInspectorDrawerOpen(false);

      updateLayout((prev) => {
        const existingRooms = prev.rooms || [];
        const result = createNewOfficeRoom({
          col,
          row,
          template,
          name: template?.name || 'New Work Zone',
          type: template?.type || 'work',
          color: template?.color || '#3b82f6',
          icon: template?.icon || '💻',
          gridWidth: prev.gridWidth,
          gridHeight: prev.gridHeight,
          existingRooms,
          autoRelocateOnOverlap: col === undefined,
        });

        if (!result.success && result.overlapConflict) {
          handleOverlapPrevented(result.room.name, result.overlapConflict.name);
          return prev;
        }

        setSelectedRoomId(result.room.id);
        return {
          ...prev,
          rooms: [...existingRooms, result.room],
        };
      });
    },
    [updateLayout, handleOverlapPrevented]
  );

  // Start Room Draft from a specific tile
  const handleStartRoomDraft = useCallback(
    (col: number, row: number) => {
      setIsInspectorDrawerOpen(false);
      const w = Math.min(4, activeLocation.gridWidth - col);
      const h = Math.min(4, activeLocation.gridHeight - row);
      setRoomDraftBounds({
        col,
        row,
        width: Math.max(1, w),
        height: Math.max(1, h),
        color: '#8b5cf6',
        name: 'New Zone',
        icon: '🏢',
      });
      setSelectedRoomId(null);
      setActiveTool('select');
    },
    [activeLocation.gridWidth, activeLocation.gridHeight]
  );

  // Dynamic Agent Quick Action from Context Menu
  const handleDynamicAgentAction = (agentId: string, actionType: DynamicActionType) => {
    updateLayout((prev) => {
      const targetAgent = prev.agents.find((a) => a.id === agentId);
      if (!targetAgent) return prev;

      const updatedAgents = prev.agents.map((ag) => {
        if (ag.id !== agentId) return ag;

        if (actionType === 'energy_boost') {
          return { ...ag, energy: 100, focus: 100 };
        }

        if (actionType === 'clear_queue') {
          return { ...ag, taskQueue: [] };
        }

        if (actionType === 'add_task') {
          const newTask: TaskItem = {
            id: `task_dyn_${Date.now()}`,
            title: '⚡ Tarea Prioritaria Asignada',
            type: 'Urgente',
            assignedAgentId: ag.id,
            progress: 0,
            createdAt: Date.now(),
            complexity: 'medium',
            status: 'in_progress',
          };
          return { ...ag, taskQueue: [newTask, ...ag.taskQueue] };
        }

        let newStatus: AgentStatus = ag.status;
        let targetItem: PlacedFurniture | undefined;

        if (actionType === 'coffee') {
          newStatus = 'coffee_break';
          targetItem = prev.items.find(
            (i) =>
              i.type === 'coffee_station' ||
              i.type === 'water_dispenser' ||
              i.type === 'breakroom_couch' ||
              i.type === 'lounge_chair' ||
              i.type === 'snack_table'
          );
        } else if (actionType === 'bathroom') {
          newStatus = 'bathroom_break';
          targetItem = prev.items.find((i) => i.type === 'toilet' || i.type === 'bathroom_stall' || i.type === 'sink');
        } else if (actionType === 'work') {
          newStatus = 'working';
          targetItem = prev.items.find((i) => i.id === ag.assignedDeskId);
        }

        const newPath = targetItem
          ? findGridPath(
              { col: Math.round(ag.currentCol), row: Math.round(ag.currentRow) },
              { col: targetItem.col, row: targetItem.row + (actionType === 'work' ? 1 : 0) },
              prev.gridWidth,
              prev.gridHeight,
              prev.items,
              prev.customFloors
            )
          : [];

        return { ...ag, status: newStatus, path: newPath };
      });

      return {
        ...prev,
        agents: updatedAgents,
      };
    });
  };

  // ==========================================
  // TIME MACHINE (DVR DEL AI SYSTEM) STATE & HANDLERS
  // ==========================================
  const [timeMachineData, setTimeMachineData] = useState<{
    frames: TimeMachineFrame[];
    incidents: TimeMachineIncident[];
  }>(() => generateSeedTimeMachineHistory(activeLocation));

  const [timeMachineFrameIndex, setTimeMachineFrameIndex] = useState<number>(() => {
    const seed = generateSeedTimeMachineHistory(activeLocation);
    return Math.max(0, seed.frames.length - 1);
  });
  const [isTimeMachineLive, setIsTimeMachineLive] = useState<boolean>(true);
  const [isSimulationPlaying, setIsSimulationPlaying] = useState<boolean>(true);
  const [showTimeMachineDrawer, setShowTimeMachineDrawer] = useState<boolean>(false);

  const handleToggleSimulationPlayPause = useCallback(() => {
    setIsSimulationPlaying((prev) => !prev);
  }, []);

  // Real-time ticking clock for top header with active timezone formatting
  const [liveClockTime, setLiveClockTime] = useState<string>(() => formatTimeInTimezone(new Date(), activeTimezoneIana));

  useEffect(() => {
    setLiveClockTime(formatTimeInTimezone(new Date(), activeTimezoneIana));

    const timer = setInterval(() => {
      setLiveClockTime(formatTimeInTimezone(new Date(), activeTimezoneIana));
    }, 1000);
    return () => clearInterval(timer);
  }, [activeTimezoneIana]);

  // Keep time machine seed frames initialized if empty or when switching to simulation
  useEffect(() => {
    if (!timeMachineData.frames || timeMachineData.frames.length === 0) {
      const generated = generateSeedTimeMachineHistory(activeLocation);
      setTimeMachineData(generated);
      setTimeMachineFrameIndex(generated.frames.length - 1);
    }
  }, [activeLocation, timeMachineData.frames]);

  // Handle Simulation Layout Updates from Live Simulation loop
  const handleSimulationLayoutUpdate = useCallback(
    (updatedLayout: OfficeLayout) => {
      updateLayout(() => updatedLayout, false);

      if (isTimeMachineLive) {
        setTimeMachineData((prev) => {
          const lastFrame = prev.frames[prev.frames.length - 1];
          const now = Date.now();
          if (!lastFrame || now - lastFrame.timestamp > 2500) {
            const newClock = formatTimeInTimezone(new Date(now), activeTimezoneIana);
            const newFrame: TimeMachineFrame = {
              id: `frame_live_${now}`,
              timestamp: now,
              clockTime: newClock,
              layoutSnapshot: JSON.parse(JSON.stringify(updatedLayout)),
              statsSnapshot: {
                totalTasksCompleted: 42,
                tasksInQueue: 3,
                throughputTasksPerMin: 14.5,
                avgSlaLatencySec: 2.2,
                officeEfficiencyPct: 93,
                activeBottlenecks: [],
                history: [],
              },
              events: [
                {
                  id: `ev_live_${now}`,
                  timestamp: now,
                  clockTime: newClock,
                  type: 'task_completed',
                  title: 'Real-Time Operation',
                  description: 'Agents processing tasks in pipeline.',
                  severity: 'info',
                },
              ],
            };

            const updatedFrames = [...prev.frames, newFrame].slice(-60);
            return {
              ...prev,
              frames: updatedFrames,
            };
          }
          return prev;
        });
      }
    },
    [updateLayout, isTimeMachineLive, activeTimezoneIana]
  );

  const handleSelectTimeMachineFrame = useCallback(
    (index: number) => {
      setTimeMachineFrameIndex(index);
      if (index === timeMachineData.frames.length - 1) {
        setIsTimeMachineLive(true);
      } else {
        setIsTimeMachineLive(false);
      }
    },
    [timeMachineData.frames.length]
  );

  const handleGoLiveTimeMachine = useCallback(() => {
    setIsTimeMachineLive(true);
    setTimeMachineFrameIndex(Math.max(0, timeMachineData.frames.length - 1));
  }, [timeMachineData.frames.length]);

  const handleJumpToIncident = useCallback((incident: TimeMachineIncident) => {
    setTimeMachineFrameIndex(incident.frameIndex);
    setIsTimeMachineLive(false);
    setShowTimeMachineDrawer(true);
  }, []);

  const handleOpenInspector = useCallback(
    (incident?: TimeMachineIncident) => {
      if (incident) {
        handleJumpToIncident(incident);
      } else {
        setShowTimeMachineDrawer(true);
      }
    },
    [handleJumpToIncident]
  );

  // Jump to simulated time when user converts time between timezones in modal
  const handleJumpToSimulatedTime = useCallback(
    (targetClockTime: string) => {
      if (timeMachineData.frames.length === 0) return;
      setMode('simulation');
      setIsTimeMachineLive(false);

      const targetShort = targetClockTime.slice(0, 5);
      const matchedIdx = timeMachineData.frames.findIndex((f) => f.clockTime.startsWith(targetShort));
      if (matchedIdx >= 0) {
        setTimeMachineFrameIndex(matchedIdx);
      } else {
        setTimeMachineFrameIndex(Math.floor(timeMachineData.frames.length / 2));
      }
    },
    [timeMachineData.frames]
  );

  // Compute what layout to display on canvas (Live vs Historical Frame)
  const currentHistoricalFrame = !isTimeMachineLive && timeMachineData.frames[timeMachineFrameIndex] ? timeMachineData.frames[timeMachineFrameIndex] : null;

  const displayLocation = currentHistoricalFrame ? currentHistoricalFrame.layoutSnapshot : activeLocation;

  const currentIncident =
    (currentHistoricalFrame &&
      timeMachineData.incidents.find((inc) => inc.clockTime === currentHistoricalFrame.clockTime || inc.frameIndex === timeMachineFrameIndex)) ||
    null;

  // === RENDER ===
  return (
    <div className="twin-shell twin-root" data-theme={visualThemeId}>
      {/* === HEADER SUPERIOR DE LA APLICACIÓN === */}
      <header ref={headerRef} className="app-header twin-shell-header">
        {/* Izquierda: Identidad de Marca */}
        <div className="twin-shell-brand">
          <div className="twin-shell-brand-icon">
            <Building2 className="twin-icon-sm" />
          </div>
          <div>
            <h1 className="twin-shell-brand-title">AVAP Digital Twin</h1>
            <p className="twin-shell-brand-subtitle">See your AI System. Simulate it. Operate it</p>
          </div>
        </div>

        {/* Centro: Selector de Modo (posicionado dinámicamente sobre el canvas) */}
        <div
          className="twin-shell-mode-switcher-wrap"
          style={{
            left: canvasCenterOffset !== null ? `${canvasCenterOffset}px` : '50%',
          }}
        >
          <div className="twin-shell-mode-switcher">
            <button onClick={() => setMode('editor')} className={`twin-shell-mode-btn ${mode === 'editor' ? 'twin-shell-mode-btn--active' : ''}`}>
              <Edit3 className="twin-icon-xs" />
              <span>Designer</span>
            </button>

            <button
              onClick={() => {
                setSelectedItem(null);
                setMode('simulation');
              }}
              className={`twin-shell-mode-btn ${mode === 'simulation' ? 'twin-shell-mode-btn--active' : ''}`}
            >
              <Activity className="twin-icon-xs" />
              <span>Simulation</span>
            </button>
          </div>
        </div>

        {/* Derecha: Menú de Cuenta de Usuario Conectado */}
        <div className="twin-shell-user-menu-wrap">
          <UserAccountMenu
            userProfile={userProfile}
            onOpenSettings={() => {
              setSettingsDrawerInitialTab('visual');
              setShowUserSettingsDrawer(true);
            }}
            onLogout={() => {
              setIsUserLoggedOut(false);
              setShowLogoutModal(true);
            }}
          />
        </div>
      </header>

      {/* === ÁREA DE TRABAJO PRINCIPAL === */}
      <div className="twin-shell-workspace">
        {/* === PANEL LATERAL IZQUIERDO (COLAPSABLE TIPO CORTINA) === */}
        <div className={`twin-shell-sidebar-outer ${isSidebarOpen ? 'twin-shell-sidebar-outer--open' : 'twin-shell-sidebar-outer--closed'}`}>
          <div className={`twin-shell-sidebar-inner ${isSidebarOpen ? 'twin-shell-sidebar-inner--visible' : 'twin-shell-sidebar-inner--hidden'}`}>
            {mode === 'editor' && (
              <OfficeEditor
                layout={activeLocation}
                onLayoutChange={updateLayout}
                activeTool={activeTool}
                setActiveTool={setActiveTool}
                selectedFurnitureType={selectedFurnitureType}
                setSelectedFurnitureType={handleSelectFurnitureTypeWithDrawer}
                selectedFloorType={selectedFloorType}
                setSelectedFloorType={handleSelectFloorTypeWithDrawer}
                floorMaterials={floorMaterials}
                onFloorMaterialsChange={setFloorMaterials}
                currentRoomName={currentRoomName}
                setCurrentRoomName={setCurrentRoomName}
                selectedItem={selectedItem}
                onSelectItem={handleSelectItemWithDrawer}
                selectedAgent={selectedAgent}
                onAgentSelect={handleSelectAgentWithDrawer}
                selectedRoomId={selectedRoomId}
                onSelectRoom={handleSelectRoomWithDrawer}
                roomDraftBounds={roomDraftBounds}
                setRoomDraftBounds={setRoomDraftBounds}
                onOverlapPrevented={handleOverlapPrevented}
                onStartSimulation={() => {
                  setSelectedItem(null);
                  setIsInspectorDrawerOpen(false);
                  setMode('simulation');
                }}
                interactionTypes={interactionTypes}
                onSaveInteraction={handleSaveInteraction}
                onDeleteInteraction={handleDeleteInteraction}
                onOpenDeleteFloorModal={(mat) => setFloorToDelete(mat)}
                onOpenDeleteFurnitureModal={(target) => {
                  const def = FURNITURE_CATALOG.find((f) => f.type === target.type);
                  setFurnitureToDelete({
                    type: target.type,
                    name: target.label || def?.name || target.type,
                    icon: target.icon || def?.icon || 'box',
                  });
                }}
                activeStep={activeEditorStep}
                onStepChange={setActiveEditorStep}
              />
            )}

            {mode === 'simulation' && (
              <OfficeSimulation
                layout={displayLocation}
                onLayoutUpdate={handleSimulationLayoutUpdate}
                selectedAgent={selectedAgent}
                initialAgentTab={selectedAgentModalTab}
                onSelectAgentModal={handleSelectAgentModal}
                onSwitchToEditor={() => setMode('editor')}
                interactionTypes={interactionTypes}
                visualThemeId={visualThemeId}
                isTimeMachineReplay={!isTimeMachineLive}
                timeMachineClockTime={currentHistoricalFrame?.clockTime}
                historicalStats={currentHistoricalFrame?.statsSnapshot}
                historicalEvents={currentHistoricalFrame?.events}
                activeIncident={currentIncident}
                onOpenInspector={handleOpenInspector}
                onGoLive={handleGoLiveTimeMachine}
                onJumpToIncident={handleJumpToIncident}
                incidentsList={timeMachineData.incidents}
                isPlaying={isSimulationPlaying}
                onTogglePlayPause={handleToggleSimulationPlayPause}
              />
            )}
          </div>
        </div>

          {/* Botón de colapsar/expandir tipo cortina */}
          <button
            type="button"
            onClick={() => setIsSidebarOpen((prev) => !prev)}
            className={`twin-shell-sidebar-toggle-btn ${isSidebarOpen ? 'twin-shell-sidebar-toggle-btn--open' : 'twin-shell-sidebar-toggle-btn--closed'}`}
            title={isSidebarOpen ? 'Collapse sidebar panel (more canvas space)' : 'Expand operations and configuration sidebar'}
            aria-label={isSidebarOpen ? 'Close sidebar panel' : 'Open sidebar panel'}
          >
            {isSidebarOpen ? <ChevronLeft className="twin-icon-sm" /> : <ChevronRight className="twin-icon-sm twin-color-theme-accent" />}
          </button>
        

        {/* === ÁREA CENTRAL: CANVAS ISOMÉTRICO 2D === */}
        <div ref={canvasAreaRef} className="twin-shell-canvas-area">
          <div className="twin-shell-canvas-inner">
            <IsometricCanvas
              layout={displayLocation}
              locationSelector={
                <div className="twin-shell-locselector-stack">
                  <button
                    type="button"
                    onClick={() => setShowTimezoneModal(true)}
                    className="twin-shell-clock-btn"
                    title={`Active Timezone: ${activeTimezoneIana} (${getTimezoneOffsetDisplay(activeTimezoneIana)}). Click to open timezone manager.`}
                  >
                    <div className="twin-shell-clock-left">
                      <Clock className="twin-icon-xs twin-shell-clock-icon" />
                      <span className="twin-shell-clock-time">{isTimeMachineLive ? liveClockTime : currentHistoricalFrame?.clockTime || liveClockTime}</span>
                      <span className="twin-shell-clock-dot">•</span>
                      <span className="twin-shell-clock-tz-name">
                        {systemTimezones.find((tz) => tz.iana === activeTimezoneIana)?.city ||
                          systemTimezones.find((tz) => tz.iana === activeTimezoneIana)?.name.split(' (')[0] ||
                          activeTimezoneIana.split('/').pop()?.replace(/_/g, ' ')}
                      </span>
                    </div>

                    <span className="twin-shell-clock-offset">{getTimezoneOffsetDisplay(activeTimezoneIana)}</span>
                  </button>

                  <LocationSelectorBar
                    locations={locations}
                    activeLocationId={activeLocationId}
                    onSelectLocation={handleSelectLocation}
                    onOpenDetailsDrawer={(locId) => {
                      handleSelectLocation(locId);
                      setLocationManagerTab('details');
                      setShowLocationManagerDrawer(true);
                    }}
                    onOpenCreateDrawer={() => {
                      setLocationManagerTab('create');
                      setShowLocationManagerDrawer(true);
                    }}
                    onOpenManagerDrawer={() => {
                      setLocationManagerTab('list');
                      setShowLocationManagerDrawer(true);
                    }}
                  />
                </div>
              }
              activeTool={mode === 'simulation' ? 'select' : activeTool}
              selectedFurnitureType={mode === 'simulation' ? null : selectedFurnitureType}
              selectedItemId={mode === 'simulation' ? null : selectedItem?.id}
              selectedAgentId={selectedAgent?.id}
              selectedRoomId={selectedRoomId}
              floorMaterialsMap={floorMaterialsMap}
              floorMaterials={floorMaterials}
              roomDraftBounds={roomDraftBounds}
              onClearTile={handleClearTile}
              onClearTileFurniture={handleClearTileFurniture}
              onClearTileFloor={handleClearTileFloor}
              onClearTileAgent={handleClearTileAgent}
              onSetTileFloor={handleSetTileFloor}
              onCreateRoom={(col, row, template) => {
                if (mode === 'simulation') setMode('editor');
                handleCreateRoom(col, row, template);
              }}
              onStartRoomDraft={(col, row) => {
                if (mode === 'simulation') setMode('editor');
                handleStartRoomDraft(col, row);
              }}
              onTileClick={mode === 'simulation' ? undefined : handleTileClick}
              onPaintTile={mode === 'simulation' ? undefined : handlePaintTile}
              onItemSelect={(item) => {
                handleSelectItemWithDrawer(item);
              }}
              onAgentSelect={handleSelectAgentModal}
              onRoomSelect={handleSelectRoomWithDrawer}
              onDeleteRoom={handleDeleteRoom}
              onRoomUpdate={mode === 'simulation' ? undefined : handleRoomUpdate}
              onOverlapPrevented={handleOverlapPrevented}
              onMoveToRoom={handleMoveAgentToRoom}
              onDynamicAgentAction={handleDynamicAgentAction}
              onDropFurniture={handleDropFurniture}
              onDropAgent={handleDropAgent}
              onUndo={handleUndo}
              canUndo={canUndo}
              onResetFloor={() => setShowResetCleanGridModal(true)}
              onOpenInteractions={() => {
                setInteractionManagerTab('catalog');
                setShowInteractionManagerModal(true);
              }}
              onOpenSnapshotManager={() => setShowSystemSnapshotDrawer(true)}
              onOpenTimeMachine={() => setShowTimeMachineDrawer(true)}
              isSimulating={mode === 'simulation'}
              isSimulationPlaying={isSimulationPlaying}
              onToggleSimulationPlayPause={handleToggleSimulationPlayPause}
              isTimeMachineReplay={!isTimeMachineLive}
              timeMachineClockTime={currentHistoricalFrame?.clockTime}
              onGoLiveTimeMachine={handleGoLiveTimeMachine}
              onOpenTimeMachineInspector={() => handleOpenInspector(currentIncident || undefined)}
              timeMachineIncidentText={currentIncident ? currentIncident.title : undefined}
              visualThemeId={visualThemeId}
              topRightOverlay={
                mode === 'simulation' ? (
                  <SystemHealthBadge
                    metrics={healthMetrics}
                    config={healthConfig}
                    onOpenHealthSettings={() => {
                      setSettingsDrawerInitialTab('health');
                      setShowUserSettingsDrawer(true);
                    }}
                    onOpenInspector={() => setIsInspectorDrawerOpen(true)}
                  />
                ) : undefined
              }
            />
          </div>
        </div>
      </div>

      {/* === DRAWER: TIME MACHINE DVR Y TIMELINE VERTICAL === */}
      <TimeMachineDrawer
        isOpen={showTimeMachineDrawer}
        onClose={() => setShowTimeMachineDrawer(false)}
        frames={timeMachineData.frames}
        incidents={timeMachineData.incidents}
        currentFrameIndex={timeMachineFrameIndex}
        isLive={isTimeMachineLive}
        onSelectFrame={handleSelectTimeMachineFrame}
        onGoLive={handleGoLiveTimeMachine}
        onSelectAgent={(agent) => handleSelectAgentModal(agent)}
        onRestoreLayoutFromFrame={(frame) => {
          updateLayout(frame.layoutSnapshot);
        }}
        activeAgents={activeLocation.agents}
      />

      {/* === MODAL: CONFIRMAR RESET / LIMPIAR PISO === */}
      <ConfirmDeleteModal
        isOpen={showResetCleanGridModal}
        onClose={() => setShowResetCleanGridModal(false)}
        onConfirm={handleResetActiveFloorToClean}
        title="Clear entire grid content?"
        itemName={`${activeLocation.name} (${activeLocation.gridWidth}x${activeLocation.gridHeight} m²)`}
        itemIcon={activeLocation.icon || '🏢'}
        description="Are you sure you want to reset this floor? All zones, resources, and custom flooring will be cleared, and placed agents will return to the unplaced pool."
        warningText="This action will completely clear the active floor grid."
        confirmText="Reset Floor"
        cancelText="Cancel"
      />

      {/* === DRAWER: GESTOR DE UBICACIONES === */}
      <LocationManagerDrawer
        isOpen={showLocationManagerDrawer}
        onClose={() => setShowLocationManagerDrawer(false)}
        locations={locations}
        activeLocationId={activeLocationId}
        onSelectLocation={handleSelectLocation}
        onCreateLocation={handleCreateLocation}
        onUpdateLocation={handleUpdateLocation}
        onDeleteLocation={handleDeleteLocation}
        onDuplicateLocation={handleDuplicateLocation}
        onTransferAgent={handleTransferAgent}
        systemTimezones={systemTimezones}
        defaultTimezone={activeTimezoneIana}
        onEnsureTimezoneRegistered={handleEnsureTimezoneRegistered}
        initialTab={locationManagerTab}
      />

      {/* === MODAL: GESTOR DE ZONAS HORARIAS === */}
      <TimezoneManagerModal
        isOpen={showTimezoneModal}
        onClose={() => setShowTimezoneModal(false)}
        systemTimezones={systemTimezones}
        timezones={systemTimezones}
        activeTimezoneIana={activeTimezoneIana}
        onSelectTimezone={handleSelectActiveTimezone}
        onSelectActiveTimezone={handleSelectActiveTimezone}
        onAddTimezone={handleAddTimezone}
        onDeleteTimezone={handleDeleteTimezone}
        currentLocationName={activeLocation?.name}
        currentLocationTimezone={activeLocation?.timezone}
        onJumpToSimulatedTime={handleJumpToSimulatedTime}
      />

      {/* === MODAL GLOBAL: GESTOR DE INTERACCIONES Y AYUDA === */}
      <InteractionManagerModal
        isOpen={showInteractionManagerModal}
        onClose={() => setShowInteractionManagerModal(false)}
        interactions={interactionTypes}
        onSaveInteraction={handleSaveInteraction}
        onDeleteInteraction={handleDeleteInteraction}
        layout={activeLocation}
        initialTab={interactionManagerTab}
      />

      {/* === MODAL: PREVENCIÓN DE SOLAPE DE ZONAS === */}
      <OverlapAlertModal
        isOpen={overlapModal.isOpen}
        onClose={() => setOverlapModal((prev) => ({ ...prev, isOpen: false }))}
        targetRoomName={overlapModal.targetRoomName}
        conflictingRoomName={overlapModal.conflictingRoomName}
        actionContext={overlapModal.actionContext}
      />

      {/* === DRAWER: SNAPSHOTS Y ESTADO JSON COMPLETO === */}
      <SystemSnapshotDrawer
        isOpen={showSystemSnapshotDrawer}
        onClose={() => setShowSystemSnapshotDrawer(false)}
        locations={locations}
        activeLocationId={activeLocationId}
        mode={mode}
        activeTimezoneIana={activeTimezoneIana}
        clockTime={liveClockTime}
        simulationSpeed={1}
        isSimulationPlaying={isSimulationPlaying}
        simulationStats={currentHistoricalFrame?.statsSnapshot}
        incidents={timeMachineData.incidents}
        timeMachineFrames={timeMachineData.frames}
        currentTimeMachineFrameIndex={timeMachineFrameIndex}
        isTimeMachineLive={isTimeMachineLive}
        systemTimezones={systemTimezones}
        userProfile={userProfile}
        onRestoreSnapshot={handleRestoreSnapshot}
      />

      {/* === DRAWER: CONFIGURACIÓN Y PREFERENCIAS DE USUARIO === */}
      <UserSettingsDrawer
        isOpen={showUserSettingsDrawer}
        onClose={() => setShowUserSettingsDrawer(false)}
        userProfile={userProfile}
        onSaveProfile={(updated) => setUserProfile(updated)}
        activeThemeId={visualThemeId}
        onSelectTheme={(newTheme) => setVisualThemeId(newTheme)}
        onOpenLocationManager={() => {
          setShowUserSettingsDrawer(false);
          setLocationManagerTab('list');
          setShowLocationManagerDrawer(true);
        }}
        onOpenTimezoneManager={() => {
          setShowUserSettingsDrawer(false);
          setShowTimezoneModal(true);
        }}
        onOpenInteractionManager={() => {
          setShowUserSettingsDrawer(false);
          setShowInteractionManagerModal(true);
        }}
        healthConfig={healthConfig}
        onSaveHealthConfig={handleSaveHealthConfig}
        healthMetrics={healthMetrics}
        initialTab={settingsDrawerInitialTab}
      />

      {/* === MODAL: CONFIRMACIÓN DE CIERRE DE SESIÓN === */}
      <UserLogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirmLogout={() => {
          setIsUserLoggedOut(true);
        }}
        userProfile={userProfile}
        isLoggedOut={isUserLoggedOut}
        onReLogin={() => {
          setIsUserLoggedOut(false);
          setShowLogoutModal(false);
        }}
      />

      {/* === DRAWER GLOBAL: INSPECTOR INFERIOR === */}
      <OfficeInspectorDrawer
        isOpen={isInspectorDrawerOpen}
        onClose={() => setIsInspectorDrawerOpen(false)}
        onOpen={() => setIsInspectorDrawerOpen(true)}
        onToggle={() => setIsInspectorDrawerOpen((prev) => !prev)}
        activeStep={activeEditorStep}
        layout={activeLocation}
        onLayoutChange={updateLayout}
        selectedRoomId={selectedRoomId}
        onSelectRoom={handleSelectRoomWithDrawer}
        onOpenDeleteRoomModal={(room) => setRoomToDelete(room)}
        onOverlapPrevented={handleOverlapPrevented}
        selectedFloorType={selectedFloorType}
        setSelectedFloorType={handleSelectFloorTypeWithDrawer}
        floorMaterials={floorMaterials}
        onFloorMaterialsChange={setFloorMaterials}
        onOpenEditFloorModal={(mat) => setFloorToEdit(mat)}
        onOpenDeleteFloorModal={(mat) => setFloorToDelete(mat)}
        selectedItem={selectedItem}
        onSelectItem={handleSelectItemWithDrawer}
        selectedFurnitureType={selectedFurnitureType}
        setSelectedFurnitureType={handleSelectFurnitureTypeWithDrawer}
        onOpenDeleteFurnitureModal={(target) => {
          const def = FURNITURE_CATALOG.find((f) => f.type === target.type);
          setFurnitureToDelete({
            type: target.type,
            name: target.label || def?.name || target.type,
            icon: target.icon || def?.icon || 'box',
          });
        }}
        selectedAgent={selectedAgent}
        onAgentSelect={handleSelectAgentWithDrawer}
        onOpenAgentSpriteModal={(ag) => setSpriteModalAgent(ag)}
        onOpenDeleteAgentModal={(ag) => setAgentToDelete(ag)}
      />

      {/* === MODAL: CONFIRMAR ELIMINAR ZONA === */}
      <ConfirmDeleteModal
        isOpen={Boolean(roomToDelete)}
        onClose={() => setRoomToDelete(null)}
        onConfirm={() => {
          if (roomToDelete) {
            handleDeleteRoom(roomToDelete.id);
            setRoomToDelete(null);
          }
        }}
        title="Delete Zone / Room?"
        itemName={roomToDelete ? roomToDelete.name : ''}
        itemIcon={roomToDelete?.icon || 'door-closed'}
        itemType="room"
        description={
          roomToDelete
            ? `Are you sure you want to delete the zone "${roomToDelete.name}" (${roomToDelete.width}x${roomToDelete.height} m²)? Objects and agents inside will remain unassigned.`
            : undefined
        }
        confirmText="Delete Room"
      />

      {/* === MODAL GLOBAL: CONFIRMAR ELIMINAR RECURSO (BORRADO MASIVO EN TODAS LAS UBICACIONES) === */}
      <ConfirmDeleteModal
        isOpen={Boolean(furnitureToDelete)}
        onClose={() => setFurnitureToDelete(null)}
        onConfirm={handleConfirmDeleteFurniture}
        title="Delete System Resource?"
        itemName={furnitureToDelete?.name || furnitureToDelete?.type || ''}
        itemIcon={furnitureToDelete?.icon || 'box'}
        itemType="furniture"
        description={
          furnitureToDelete
            ? `Are you sure you want to delete the resource "${furnitureToDelete.name || furnitureToDelete.type}"? All placed instances (${furnitureInstancesCount} item${furnitureInstancesCount === 1 ? '' : 's'}) of this type across all floors will be removed.`
            : undefined
        }
        confirmText="Delete Resource and Items"
      />

      {/* === MODAL GLOBAL: CONFIRMAR ELIMINAR MATERIAL DE PISO (BORRADO MASIVO) === */}
      <ConfirmDeleteModal
        isOpen={Boolean(floorToDelete)}
        onClose={() => setFloorToDelete(null)}
        onConfirm={handleConfirmDeleteFloor}
        title="Delete Floor Material?"
        itemName={floorToDelete ? floorToDelete.name : ''}
        itemIcon={floorToDelete?.icon || '🎨'}
        itemType="room"
        description={
          floorToDelete
            ? `Are you sure you want to delete the material "${floorToDelete.name}"? All painted tiles (${floorTilesCount} tile${floorTilesCount === 1 ? '' : 's'}) with this material across all floors will be cleared.`
            : undefined
        }
        confirmText="Delete Material and Tiles"
      />

      {/* === MODAL: CONFIRMAR ELIMINAR INSTANCIA (RESPALDO) === */}
      <ConfirmDeleteModal
        isOpen={Boolean(itemToDelete)}
        onClose={() => {}}
        onConfirm={() => {
          if (itemToDelete) {
            const updatedItems = activeLocation.items.filter((it) => it.id !== itemToDelete.id);
            updateLayout({ ...activeLocation, items: updatedItems });
            if (selectedItem?.id === itemToDelete.id) {
              setSelectedItem(null);
            }
          }
        }}
        title="Delete Instance?"
        itemName={(() => {
          if (!itemToDelete) return '';
          const def = FURNITURE_CATALOG.find((f) => f.type === itemToDelete.type);
          return itemToDelete.label || def?.name || itemToDelete.type;
        })()}
        itemIcon={(() => {
          if (!itemToDelete) return 'box';
          const def = FURNITURE_CATALOG.find((f) => f.type === itemToDelete.type);
          return def?.icon || 'box';
        })()}
        itemType="furniture"
        description={(() => {
          if (!itemToDelete) return undefined;
          const def = FURNITURE_CATALOG.find((f) => f.type === itemToDelete.type);
          const name = itemToDelete.label || def?.name || itemToDelete.type;
          return `Are you sure you want to delete this instance of "${name}" (at coordinate ${itemToDelete.col}, ${itemToDelete.row}) from the current floor?`;
        })()}
        confirmText="Delete Instance"
      />

      {/* === MODAL: CONFIRMAR DESACTIVAR/RETIRAR AGENTE === */}
      <ConfirmDeleteModal
        isOpen={Boolean(agentToDelete)}
        onClose={() => setAgentToDelete(null)}
        onConfirm={() => {
          if (agentToDelete) {
            const updatedAgents = activeLocation.agents.map((a) =>
              a.id === agentToDelete.id
                ? {
                    ...a,
                    isPlaced: false,
                    currentCol: -1,
                    currentRow: -1,
                    targetCol: null,
                    targetRow: null,
                    path: [],
                    status: 'idle' as const,
                  }
                : a
            );
            updateLayout({ ...activeLocation, agents: updatedAgents });
            if (selectedAgent?.id === agentToDelete.id) {
              setSelectedAgent(null);
            }
            setAgentToDelete(null);
          }
        }}
        title="Deactivate and Remove from Grid?"
        itemName={agentToDelete ? agentToDelete.nickname?.trim() || agentToDelete.name : ''}
        itemIcon={(agentToDelete as any)?.avatar || 'bot'}
        itemType="agent"
        description={
          agentToDelete
            ? `Do you want to remove agent "${agentToDelete.nickname?.trim() || agentToDelete.name}" from the grid? The agent will return to the unplaced agent pool in standby mode.`
            : undefined
        }
        confirmText="Deactivate and Remove"
      />

      {/* === DRAWER: EDITAR MATERIAL DE PISO === */}
      {floorToEdit && (
        <FloorMaterialDrawer
          isOpen={Boolean(floorToEdit)}
          initialMaterial={floorToEdit}
          onClose={() => setFloorToEdit(null)}
          onSave={(updated) => {
            setFloorMaterials((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
            setFloorToEdit(null);
          }}
          onDelete={(mat) => {
            setFloorToEdit(null);
            setFloorToDelete(mat);
          }}
        />
      )}

      {/* === MODAL: SPRITES DEL AGENTE === */}
      {spriteModalAgent && (
        <AgentAvatarSpriteModal
          agent={spriteModalAgent}
          isOpen={Boolean(spriteModalAgent)}
          onClose={() => setSpriteModalAgent(null)}
          onSaveSpriteSet={(agentId, spriteSet) => {
            updateLayout({
              ...activeLocation,
              agents: activeLocation.agents.map((ag) => (ag.id === agentId ? { ...ag, spriteSet } : ag)),
            });
            if (selectedAgent?.id === agentId) {
              setSelectedAgent((prev) => (prev ? { ...prev, spriteSet } : null));
            }
            setSpriteModalAgent(null);
          }}
        />
      )}
    </div>
  );
}